import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";

type SocialPlatform = "linkedin" | "facebook" | "x";

interface JobFormState {
  // Step 1 Fields
  title: string;
  jobTitleId?: number | null;
  customTitle?: string;
  company_name: string;
  sector_id: any | null;
  location_country: string;
  location_city: string;
  level: string;
  type: string;
  employment_type: string;
  experience: any | null;
  language: string;
  expiryDate: dayjs.Dayjs | null;

  // Step 2 Fields
  description: string;
  skills: Array<{ name: string }>;

  // NEW: Description method fields
  descriptionInputType: "upload" | "manual" | null;
  descriptionFile: any | null;

  // Social auto-post
  social_auto_post: boolean;
  social_platforms: SocialPlatform[];
  socialDefaultsHydrated: boolean;

  // Metadata
  currentStep: number;
  isEditing: boolean;
  jobId?: any;

  // Actions
  updateStep1: (
    data: Partial<
      Omit<
        JobFormState,
        | "description"
        | "skills"
        | "descriptionInputType"
        | "descriptionFile"
        | "currentStep"
        | "isEditing"
        | "jobId"
        | "social_auto_post"
        | "social_platforms"
        | "socialDefaultsHydrated"
      >
    >,
  ) => void;
  updateStep2: (data: {
    description?: string;
    skills?: Array<{ name: string }>;
    descriptionInputType?: "upload" | "manual" | null;
    descriptionFile?: any | null;
  }) => void;
  setSocialAutoPost: (enabled: boolean) => void;
  setSocialPlatforms: (platforms: SocialPlatform[]) => void;
  hydrateSocialDefaults: (defaults: {
    auto_post_enabled_by_default: boolean;
    default_platforms: SocialPlatform[];
  }) => void;
  setCurrentStep: (step: number) => void;
  setIsEditing: (editing: boolean) => void;
  setJobId: (id: any) => void;
  resetForm: () => void;
  loadFromAPI: (jobData: any, options?: { asNewListing?: boolean }) => void;
}

const initialFormState = {
  title: "",
  jobTitleId: null,
  company_name: "",
  sector_id: null,
  location_country: "",
  location_city: "",
  level: "",
  type: "",
  employment_type: "",
  experience: null,
  language: "",
  expiryDate: null,
  description: "",
  skills: [],
  descriptionInputType: null as "upload" | "manual" | null,
  descriptionFile: null,
  social_auto_post: false,
  social_platforms: ["linkedin", "facebook", "x"] as SocialPlatform[],
  socialDefaultsHydrated: false,
  currentStep: 1,
  isEditing: false,
  jobId: undefined,
};

export const useJobFormStore = create<JobFormState>()(
  persist(
    (set, get) => ({
      ...initialFormState,

      updateStep1: (data) =>
        set((state) => ({
          ...state,
          ...data,
        })),

      updateStep2: (data) =>
        set((state) => ({
          ...state,
          ...data,
        })),

      setSocialAutoPost: (enabled) => set({ social_auto_post: enabled }),

      setSocialPlatforms: (platforms) => set({ social_platforms: platforms }),

      hydrateSocialDefaults: (defaults) => {
        if (get().socialDefaultsHydrated || get().isEditing) {
          return;
        }
        set({
          social_auto_post: Boolean(defaults.auto_post_enabled_by_default),
          social_platforms:
            defaults.default_platforms?.length > 0
              ? defaults.default_platforms
              : ["linkedin", "facebook", "x"],
          socialDefaultsHydrated: true,
        });
      },

      setCurrentStep: (step) => set({ currentStep: step }),

      setIsEditing: (editing) => set({ isEditing: editing }),

      setJobId: (id) => set({ jobId: id }),

      resetForm: () => set(initialFormState),

      loadFromAPI: (jobData, options) => {
        const asNew = options?.asNewListing === true;
        const descText = jobData.description || jobData.jobsummary || "";
        return set({
          title: jobData.title,
          jobTitleId: jobData.jobtitle_id || null,
          company_name: jobData.company_name,
          sector_id: jobData.sector_id,
          location_country: jobData.location_country,
          location_city: jobData.location_city,
          level: jobData.level,
          type: jobData.type,
          employment_type: jobData.employment_type,
          experience: jobData.years_of_experience,
          language: jobData.language,
          expiryDate: jobData.expiry_date ? dayjs(jobData.expiry_date) : null,
          description: descText,
          skills: jobData.skills || [],
          descriptionInputType: descText ? "manual" : null,
          descriptionFile: null,
          social_auto_post: Boolean(jobData.social_auto_post),
          social_platforms:
            Array.isArray(jobData.social_platforms) &&
            jobData.social_platforms.length > 0
              ? jobData.social_platforms
              : ["linkedin", "facebook", "x"],
          socialDefaultsHydrated: true,
          isEditing: !asNew,
          jobId: asNew ? undefined : jobData.id,
        });
      },
    }),
    {
      name: "job-creation-form",
      partialize: (state) => ({
        title: state.title,
        jobTitleId: state.jobTitleId,
        company_name: state.company_name,
        sector_id: state.sector_id,
        location_country: state.location_country,
        location_city: state.location_city,
        level: state.level,
        type: state.type,
        employment_type: state.employment_type,
        experience: state.experience,
        language: state.language,
        expiryDate: state.expiryDate,
        description: state.description,
        skills: state.skills,
        descriptionInputType: state.descriptionInputType,
        social_auto_post: state.social_auto_post,
        social_platforms: state.social_platforms,
        socialDefaultsHydrated: state.socialDefaultsHydrated,
        currentStep: state.currentStep,
        isEditing: state.isEditing,
        jobId: state.jobId,
      }),
    },
  ),
);
